// The practice league's weeks gone by (B-527). A player's points in a week
// gone by are his own (`weekPoints`), drawn from his id and the week, which
// his card's log and the Players list already read; a team's score that week
// is its starters' points, its bench its bench's, and its best lineup the
// best its players could have made (`bestOf`). Until 2026-10-26 a past week's
// score was a number drawn for the team with nobody's points behind it, so the
// tour had no box score and no Max PF for it.
//
// THE LINEUP OF A WEEK GONE BY is kept by week (`SimState.pastLineups`): the
// one each team was made with, and last week's as it stood at that week's
// Sunday kickoff, after the moves the league replays into that week
// (`keepWeek`, from `initWaivers`). A box score that followed today's lineup
// would change under a swap made now, and one that ignored last week's moves
// would list a player the log says was let go before the games.

import { bestLineup } from '@fsp/domain';
import { football } from '@fsp/sports-football';
import { weekSlots, type ProGame } from './games';
import type { PeriodTeamDetail } from './history';
import type { SimPlayer, SimState, SimTeam } from './league';
import { hashSeed, rng } from './rng';

const slotDefs = new Map(football.slots.map((s) => [s.id, s]));
const round1 = (n: number) => Math.round(n * 10) / 10;

/** A team's lineup rows, as `SimTeam.lineup` holds them. */
export type LineupRows = SimTeam['lineup'];

/**
 * The draws a pool row makes for a player, in the order it makes them: one
 * seed per player, so ownership, trend and his season's form are the same
 * numbers wherever they are asked for.
 */
export function poolDraws(p: SimPlayer): {
  ownershipPct: number;
  trend: 'up' | 'down' | 'flat';
  form: number;
} {
  const r = rng(hashSeed(p.id, 'own'));
  const ownershipPct = Math.max(1, Math.min(100, Math.round(p.ppg * 5 + r.around(0, 12))));
  const trend = r.chance(0.35) ? 'up' : r.chance(0.4) ? 'down' : 'flat';
  const form = r.around(1, 0.15, 0.6, 1.4);
  return { ownershipPct, trend, form };
}

/**
 * A player's points in one week gone by, as the demo makes them up: the
 * season's form (one draw the player keeps all season) times the week's own
 * swing, to a tenth. Each week draws from its own seed, so week 3 is the same
 * number in week 5 and in week 9: a history, not a reroll. Every week gone by
 * counts as a game with a line; the demo keeps no byes or absences for them.
 */
export function weekPoints(p: SimPlayer, period: number, form = poolDraws(p).form): number {
  return round1(p.ppg * form * rng(hashSeed(p.id, 'week', period)).around(1, 0.35, 0, 2.5));
}

/** A player's points in each period already played, oldest first. */
export function pastWeekPoints(p: SimPlayer, played: number): number[] {
  const form = poolDraws(p).form;
  const weeks: number[] = [];
  for (let period = 1; period <= played; period++) weeks.push(weekPoints(p, period, form));
  return weeks;
}

/**
 * A week gone by's pro games, at that week's kickoffs: every club playing,
 * since the demo keeps no byes for a week gone by (`weekPoints` gives every
 * player a game), drawn from the world's seed for the week (`seed`), so every
 * league of one world played the same games.
 */
export function pastProGames(
  clubs: readonly string[],
  sundayUtcMidnight: number,
  seed: number,
): ProGame[] {
  const r = rng(hashSeed(seed, 'past-schedule'));
  const order = r.shuffle(clubs);
  const slots = weekSlots(sundayUtcMidnight);
  // Thursday night, nine at 1:00, two at 4:05 and 4:25, Sunday night and
  // Monday night; a longer list of clubs fills the 1:00 window.
  const plan = [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2, 2, 3, 3, 4, 5];
  const games: ProGame[] = [];
  for (let i = 0; i * 2 + 1 < order.length; i++) {
    const home = order[i * 2]!;
    const away = order[i * 2 + 1]!;
    const slot = slots[plan[i] ?? 1]!;
    games.push({
      id: `g-${home}-${away}-${sundayUtcMidnight}`,
      home,
      away,
      startAt: slot.at,
      homeFinal: r.int(13, 34),
      awayFinal: r.int(10, 31),
    });
  }
  return games;
}

/**
 * The best lineup a team's players could have made (B-419's *Max PF*, the
 * awards' *left on the bench*): its starting slots, each the most points
 * among its starters and bench who fit, the narrowest slot first
 * (`bestLineup`). Never a player on IR.
 */
export function bestOf(
  lineup: LineupRows,
  players: ReadonlyMap<string, SimPlayer>,
  pointsOf: (p: SimPlayer) => number,
): number {
  const counts = new Map<string, number>();
  for (const row of lineup)
    if (slotDefs.get(row.slot)?.kind === 'starter')
      counts.set(row.slot, (counts.get(row.slot) ?? 0) + 1);
  const candidates = lineup.flatMap((row) => {
    const kind = slotDefs.get(row.slot)?.kind;
    const p = row.playerId === null ? undefined : players.get(row.playerId);
    if (p === undefined || (kind !== 'starter' && kind !== 'bench')) return [];
    return [
      { id: p.id, positions: p.positions, points: pointsOf(p), rank: kind === 'starter' ? 0 : 1 },
    ];
  });
  return round1(
    bestLineup(
      [...counts].map(([slot, count]) => ({
        id: slot,
        count,
        kind: 'starter',
        eligible: slotDefs.get(slot)?.eligible ?? [],
      })),
      candidates,
    ).total,
  );
}

/** The details behind a week gone by's total (`PeriodTeamDetail`), from `pastWeek`. */
export function detailOf(week: ReturnType<typeof pastWeek>): PeriodTeamDetail {
  return {
    bench: week.bench,
    optimal: week.optimal,
    topPlayerId: week.topPlayerId,
    topPoints: week.topPoints,
    projected: week.projected,
    emptyStarters: week.emptyStarters,
    // Nobody was inactive in a week gone by: the tour keeps no injury report for one.
    inactiveStarters: 0,
  };
}

/**
 * Keep `period`'s lineups as they stand now, and its finals and details from
 * them: last week's, at its Sunday kickoff, once the moves made before it have
 * landed (`initWaivers`).
 */
export function keepWeek(state: SimState, period: number): void {
  if (period < 1) return;
  for (const team of state.teams) {
    const key = `${period}:${team.id}`;
    if (!state.finals.has(key)) continue;
    const lineup = team.lineup.map((row) => ({ ...row }));
    state.pastLineups.set(key, lineup);
    const week = pastWeek(lineup, state.players, period);
    state.finals.set(key, week.score);
    state.periodDetails.set(key, detailOf(week));
  }
}

/**
 * What a player scored for a team in a week gone by as one of its starters
 * (the awards' pickups, as a real league counts them): his points that week
 * when that week's lineup started him, else nothing.
 */
export function startedPoints(
  state: SimState,
  teamId: string,
  playerId: string,
  period: number,
): number {
  const lineup = state.pastLineups.get(`${period}:${teamId}`) ?? [];
  const started = lineup.some(
    (row) => row.playerId === playerId && slotDefs.get(row.slot)?.kind === 'starter',
  );
  const p = state.players.get(playerId);
  return started && p !== undefined ? weekPoints(p, period) : 0;
}

/** One team's week gone by, from its lineup then and its players' points that week. */
export function pastWeek(
  lineup: LineupRows,
  players: ReadonlyMap<string, SimPlayer>,
  period: number,
): {
  score: number;
  bench: number;
  optimal: number;
  topPlayerId: string | null;
  topPoints: number;
  projected: number;
  emptyStarters: number;
} {
  const kindOf = (slot: string) => slotDefs.get(slot)?.kind;
  const rowsOf = (kind: string) =>
    lineup.flatMap((row) => {
      if (kindOf(row.slot) !== kind) return [];
      const p = row.playerId === null ? undefined : players.get(row.playerId);
      return p === undefined ? [] : [{ p, points: weekPoints(p, period) }];
    });
  const starters = rowsOf('starter');
  const bench = rowsOf('bench');
  const score = round1(starters.reduce((s, r) => s + r.points, 0));
  const top = [...starters].sort((a, b) => b.points - a.points)[0];
  return {
    score,
    bench: round1(bench.reduce((s, r) => s + r.points, 0)),
    // Never below the score: the lineup started is one the best could be.
    optimal: Math.max(
      score,
      bestOf(lineup, players, (p) => weekPoints(p, period)),
    ),
    topPlayerId: top?.p.id ?? null,
    topPoints: top?.points ?? 0,
    projected: round1(starters.reduce((s, r) => s + r.p.ppg, 0)),
    emptyStarters: lineup.filter((row) => kindOf(row.slot) === 'starter' && row.playerId === null)
      .length,
  };
}
